import { DownOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Button, Collapse, Spin } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { queries } from 'services/queries';
import { FootprintTimelineItem } from './FootprintTimelineItem';
import { PanelHeader } from './PanelHeader';

interface FootprintsPanelProps {
  storeId: Conversation.Conversation['storeId'];
  externalBuyerId: Conversation.Conversation['externalBuyerId'];
}

export const FootprintsPanel = (props: FootprintsPanelProps) => {
  const { storeId, externalBuyerId } = props;
  const { t } = useTranslate();
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);

  const {
    data: footprintData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...queries.jolectBuyerCenter.footprint({
      storeId: Number(storeId) || 0,
      sgNewClientId: externalBuyerId || '',
    }),
    enabled: !!externalBuyerId && !!storeId,
  });

  const items = footprintData?.pageViewed ?? [];
  const hasItems = items.length > 0;

  useEffect(() => {
    setActiveKey(hasItems ? 'Footprints' : undefined);
  }, [hasItems]);

  return (
    <Collapse
      activeKey={activeKey}
      className={cx(styles, 'flex-shrink-0')}
      bordered={false}
      expandIconPosition="right"
      expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
      onChange={(key) => setActiveKey(key as string)}
    >
      <Collapse.Panel
        header={
          <PanelHeader
            title={t('scene.chatWorkspace.buyerActivityTab.footprints.title')}
            count={items.length}
          />
        }
        key="Footprints"
      >
        {isError ? (
          <div className="flex flex-col items-center gap-2 py-6 text-[#B5B9BF]">
            <span>{t('common.feedback.loadFailedRetry')}</span>
            <Button type="link" onClick={() => refetch()}>
              {t('common.action.retry')}
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-6">
            <Spin />
          </div>
        ) : hasItems ? (
          <div className="max-h-[132px] overflow-y-auto">
            <div className="mx-3 mb-3 py-2 rounded-[4px] bg-[#F7F9FA]">
              {items.map((page, index) => (
                <FootprintTimelineItem
                  key={`${index}-${page.href}-${page.timestamp}`}
                  title={page.title}
                  timestamp={page.timestamp}
                  isLast={index === items.length - 1}
                  pageUrl={page.href}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-[#B5B9BF]">
            {t('scene.chatWorkspace.buyerActivityTab.footprints.emptyDescription')}
          </div>
        )}
      </Collapse.Panel>
    </Collapse>
  );
};

const styles = css`
  background-color: #ffffff;

  .ant-collapse-item {
    border: none;
  }

  .ant-collapse-item > .ant-collapse-header {
    padding-top: 11px;
    padding-bottom: 11px;
    padding-left: 12px;
    font-weight: 500;
    color: #232426;

    .anticon {
      right: 12px;
    }
  }

  .ant-collapse-content-box {
    padding-left: 0;
    padding-right: 0;
  }
`;
